import { IAssetCategoryTypeResponse } from '@kcftech/prometheus-client';
import {
    TextInlineEditor,
    TextInlineEditorProps,
} from 'components/inline_edit/TextInlineEditor';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useDebounceCallback } from 'custom_hooks/useDebounceCallback';
import { updateAssetType } from 'utils/asset_classification';
import { Filter } from 'utils/query_v3';

export type AssetTypeNameInlineEditorProps = Omit<
    TextInlineEditorProps,
    'value' | 'onConfirm' | 'validate'
> &
    Readonly<{
        assetType: IAssetCategoryTypeResponse;
        onUpdate: () => void;
    }>;

export function AssetTypeNameInlineEditor({
    assetType,
    onUpdate,
    ...rest
}: AssetTypeNameInlineEditorProps) {
    const { canEditAssetType } = usePermissions();

    const debouncedValidate = useDebounceCallback(200, (value) => validate(value));

    const onSave = async (updatedName: string | undefined) => {
        if (!updatedName) {
            throw new Error('Missing name');
        }
        try {
            await updateAssetType(assetType.Id, {
                Name: updatedName.trim(),
            });
            onUpdate();
        } catch {
            return 'Unable to save asset type name';
        }

        return undefined;
    };

    const validate = async (val: string | undefined) => {
        if (!val || val.trim() === '') {
            return 'Name is required';
        }

        if (val.length > 50) {
            return 'Name must be 50 characters or less';
        }

        const assetTypeNameFiltered = await sdClient.getAssetClassificationTypes({
            filter: Filter.eq('Name', val),
        });

        if (assetTypeNameFiltered.Result.some(({ Id }) => Id !== assetType.Id)) {
            return 'Name already exists. Please enter a unique name.';
        }

        return undefined;
    };

    return (
        <TextInlineEditor
            value={assetType.Name}
            onConfirm={onSave}
            isDisabled={!canEditAssetType}
            validate={async (value) => debouncedValidate(value)}
            {...rest}
        />
    );
}
